import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share } from "rxjs/operators";
import { AuthenticationService } from '../AuthenticationService';
import { VendorDelegate } from "./vendor.delegate";
import { IVendor, INewVendor, IVendorContact, IVendorResponse, INewVendorContractDocument, IVendorOtherContact, INewVendorBulk } from "./vendor.interface";

export class VendorService
{
    private vendorsData: IVendor[] = null;
    private vendorsSubject: BehaviorSubject<IVendor[]> = new BehaviorSubject(null);
    public vendors: Observable<IVendor[]> = this.vendorsSubject;

    private vendorsFilteredSubject: BehaviorSubject<IVendor[]> = new BehaviorSubject(null);
    public vendorsFiltered: Observable<IVendor[]> = this.vendorsFilteredSubject;

    private selectedVendorId: string = "0";


    private selectedVendorData: IVendor = null;
    private selectedVendorSubject: BehaviorSubject<IVendor> = new BehaviorSubject(this.selectedVendorData);
    public selectedVendor: Observable<IVendor> = this.selectedVendorSubject;

    constructor(private vendorDelegate: VendorDelegate, private authenticationService: AuthenticationService)
    {
    }

    public get vendorId(): string
    {
        return this.selectedVendorId;
    }

    public setSelectedVendorId(vendorId: string): void
    {
        this.selectedVendorId = vendorId;
    }

    public getSelectedVendorById(vendorId: string): Observable<boolean>
    {
        if (vendorId === "0")
        {
            this.selectedVendorData = null;
            this.selectedVendorSubject.next(this.selectedVendorData);
            return observableOf(false);
        }

        var response = this.vendorDelegate.getVendorById(vendorId);

        return this.processVendor(response);

    }

    public getAllVendors(): Observable<boolean>
    {
        if (this.vendorsData !== null && this.vendorsData.length > 0)
        {
            return of(true);
        }
        const response = this.vendorDelegate.getAllVendors();
        return this.processVendors(response);
    }

    public createNewContractDocuments(documents: INewVendorContractDocument): Observable<boolean>
    {
        const response = this.vendorDelegate.createVendorContracts({
            ...documents,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public createNewVendorsBulk(benchLead: INewVendorBulk): Observable<boolean>
    {
        const response = this.vendorDelegate.createNewVendorsBulk({
            ...benchLead,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendors(response);
    }

    public createNewVendor(vendor: INewVendor): Observable<boolean>
    {
        const response = this.vendorDelegate.createNewVendor({
            ...vendor,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendors(response);
    }

    public updateVendor(vendor: IVendor): Observable<boolean>
    {
        const response = this.vendorDelegate.updateVendor({
            ...vendor,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public createOtherContact(vendorContact: IVendorOtherContact): Observable<boolean>
    {
        const response = this.vendorDelegate.createOtherVendorContact({
            ...vendorContact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public updateOtherContact(vendorContact: IVendorOtherContact): Observable<boolean>
    {
        const response = this.vendorDelegate.updateOtherVendorContact({
            ...vendorContact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public createNewContact(vendorContact: IVendorContact): Observable<boolean>
    {
        const response = this.vendorDelegate.createVendorContact({
            ...vendorContact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public updateContact(vendorContact: IVendorContact): Observable<boolean>
    {
        const response = this.vendorDelegate.updateVendorContact({
            ...vendorContact,
            ...this.authenticationService.getBaseRequest
        });
        return this.processVendor(response);
    }

    public filterVendors(searchTerm: string, filterBy: string)
    {
        let data = this.vendorsData;
        if (filterBy == null)
        {
            data = this.vendorsData.filter(x =>
                x.vendorName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else 
        {
            data = this.vendorsData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.vendorsFilteredSubject.next(data);
    }

    private processVendors(vendors: Observable<IVendorResponse>): Observable<boolean>
    {
        vendors.subscribe(onSuccess.bind(this), onFault.bind(this));

        return vendors.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError((error) => {throw error}),
            share());

        function onSuccess(vendorResponse: IVendorResponse)
        {
            if (vendorResponse.success && vendorResponse.vendors.length > 0)
            {
                this.selectedVendorId = vendorResponse.vendorId ? vendorResponse.vendorId : this.selectedVendorId;
                this.vendorsData = vendorResponse.vendors;
                this.vendorsSubject.next(this.vendorsData);
                this.selectedVendorData = this.vendorsData.find(x => x.vendorId === this.selectedVendorId);
                this.selectedVendorSubject.next(this.selectedVendorData);

                this.vendorsFilteredSubject.next(this.vendorsData);
            }
        }

        function onFault(error: any)
        {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processVendor(vendors: Observable<IVendorResponse>): Observable<boolean>
    {
        vendors.subscribe(onSuccess.bind(this), onFault.bind(this));

        return vendors.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError((error) =>{ throw error}),
            share());

        function onSuccess(vendorResponse: IVendorResponse)
        {
            if (vendorResponse.success && vendorResponse.vendor)
            {
                this.selectedVendorId = vendorResponse.vendorId ? vendorResponse.vendorId : this.selectedVendorId;
                this.selectedVendorData = vendorResponse.vendor;
                this.selectedVendorSubject.next(this.selectedVendorData);
            }
        }

        function onFault(error: any)
        {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }
}